// Bewertungen auf der Produktseite (sections/kunnie-reviews.liquid): lädt Zusammenfassung und Liste von kunnie-api
// (GET /apps/kunnie/reviews?product=&page=, reviews.ts), sobald der Abschnitt in die Nähe des Bildschirms kommt, zeigt je
// nach Anmeldung und Kauf „Bewertung schreiben“, das Formular oder einen Hinweis und sendet Bewertungen
// (POST /apps/kunnie/reviews). Mit data-scope="shop" (Seite „Bewertungen“) alle Produkte (?all=1), jede Bewertung mit
// Produktlink, ohne Schreiben. Texte kommen aus der Section (<script data-texts>), Ausgaben werden escaped.

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
const fill = (text, values) => String(text ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => values[key] ?? '');
const reviewLocale = document.documentElement.lang || 'de';
const ratingText = (number) => new Intl.NumberFormat(reviewLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(number);
const dateText = (iso) =>
  new Intl.DateTimeFormat(reviewLocale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`));
const stars = (rating) => `<span class="kunnie-rating__stars" style="--kunnie-rating: ${(rating / 5) * 100}%;" aria-hidden="true"></span>`;

class KunnieReviews extends HTMLElement {
  connectedCallback() {
    this.texts = JSON.parse(this.querySelector('[data-texts]')?.textContent || '{}');
    this.list = this.querySelector('[data-list]');
    this.more = this.querySelector('[data-more]');
    this.write = this.querySelector('[data-write]');
    this.summary = this.querySelector('[data-summary]');
    this.page = 0;
    this.shop = this.dataset.scope === 'shop';
    this.more?.addEventListener('click', () => this.load(this.page + 1));
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load(1);
      },
      { rootMargin: '600px 0px' },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async request(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json', ...(options.headers || {}) }, ...options });
    return response.json();
  }

  async load(page) {
    let data;
    try {
      const scope = this.shop ? 'all=1' : `product=${encodeURIComponent(this.dataset.productId)}`;
      data = await this.request(`${this.dataset.endpoint}?${scope}&page=${page}`);
    } catch {
      return; // Liste bleibt leer, die Zusammenfassung aus den Metafeldern steht
    }
    if (!data || data.error) return;
    if (page === 1) {
      this.renderSummary(data.summary);
      if (!this.shop) this.renderWrite(data.me);
      this.list.innerHTML = '';
    }
    this.list.insertAdjacentHTML('beforeend', data.reviews.map((review) => this.reviewHtml(review)).join(''));
    this.page = page;
    this.more.hidden = !data.hasMore;
  }

  renderSummary(summary) {
    if (!summary?.count) {
      this.summary.innerHTML = `<p class="kunnie-reviews__empty">${escapeHtml(this.texts.empty)}</p>`;
      return;
    }
    const bars = summary.distribution
      .map((n, index) => {
        const starsCount = 5 - index;
        const percent = Math.round((n / summary.count) * 100);
        return `<li class="kunnie-reviews__bar"><span aria-hidden="true">${starsCount} ★</span><span class="visually-hidden">${escapeHtml(fill(this.texts.stars, { count: starsCount }))}:</span><span class="kunnie-reviews__track"><span class="kunnie-reviews__fill" style="inline-size: ${percent}%;"></span></span><span>${n}</span></li>`;
      })
      .join('');
    this.summary.innerHTML = `
      <div class="kunnie-reviews__overview">
        <div class="kunnie-reviews__score">
          <span class="kunnie-reviews__average">${ratingText(summary.average)}</span>
          <span class="kunnie-rating kunnie-rating--summary">${stars(summary.average)}</span>
          <span>${escapeHtml(fill(summary.count === 1 ? this.texts.summaryOne : this.texts.summary, { count: summary.count }))}</span>
        </div>
        <ul class="kunnie-reviews__bars" role="list" aria-label="${escapeHtml(this.texts.distribution)}">${bars}</ul>
      </div>`;
  }

  renderWrite(me) {
    this.write.hidden = false;
    if (me?.canReview) {
      this.write.innerHTML = `<button class="button" type="button" data-open>${escapeHtml(this.texts.writeButton)}</button>`;
      this.write.querySelector('[data-open]').addEventListener('click', () => this.showForm());
    } else if (!me?.loggedIn) {
      this.write.innerHTML = `<p class="kunnie-reviews__note">${escapeHtml(this.texts.loginHint)}</p><a class="button button-secondary" href="${escapeHtml(this.dataset.loginUrl)}">${escapeHtml(this.texts.loginButton)}</a>`;
    } else {
      const text = me.reason === 'already_reviewed' ? this.texts.alreadyReviewed : this.texts.notPurchased;
      this.write.innerHTML = `<p class="kunnie-reviews__note">${escapeHtml(text)}</p>`;
    }
  }

  showForm() {
    const radios = [5, 4, 3, 2, 1]
      .map(
        (value) =>
          `<input type="radio" name="rating" id="kunnie-review-${value}" value="${value}" required><label for="kunnie-review-${value}" title="${escapeHtml(fill(this.texts.stars, { count: value }))}"><span aria-hidden="true">★</span><span class="visually-hidden">${escapeHtml(fill(this.texts.stars, { count: value }))}</span></label>`,
      )
      .join('');
    this.write.innerHTML = `
      <form class="kunnie-reviews__form" novalidate>
        <fieldset class="kunnie-reviews__rating"><legend>${escapeHtml(this.texts.formRating)}</legend><div class="kunnie-reviews__picker">${radios}</div></fieldset>
        <label>${escapeHtml(this.texts.formTitle)}<input type="text" name="title" maxlength="120"></label>
        <label>${escapeHtml(this.texts.formBody)}<textarea name="body" required minlength="10" maxlength="3000"></textarea></label>
        <label>${escapeHtml(this.texts.formAuthor)}<input type="text" name="author" required maxlength="40" autocomplete="nickname" value="${escapeHtml(this.dataset.author)}"></label>
        <p class="kunnie-reviews__hint">${escapeHtml(this.texts.formAuthorHint)}</p>
        <p class="kunnie-reviews__error" role="alert" hidden></p>
        <div class="kunnie-reviews__actions">
          <button class="button" type="submit">${escapeHtml(this.texts.formSubmit)}</button>
          <button class="button button-secondary" type="button" data-cancel>${escapeHtml(this.texts.formCancel)}</button>
        </div>
      </form>`;
    const form = this.write.querySelector('form');
    form.querySelector('[data-cancel]').addEventListener('click', () => this.renderWrite({ loggedIn: true, canReview: true }));
    form.addEventListener('submit', (event) => this.submit(event, form));
    form.addEventListener('input', () => {
      form.querySelector('.kunnie-reviews__error').hidden = true;
    });
    form.querySelector('input[name="rating"]')?.focus();
  }

  async submit(event, form) {
    event.preventDefault();
    const error = form.querySelector('.kunnie-reviews__error');
    const values = Object.fromEntries(new FormData(form));
    const show = (code) => {
      error.textContent = this.texts.errors?.[code] || this.texts.errors?.default || '';
      error.hidden = false;
    };
    if (!values.rating) return show('invalid_rating');
    if (String(values.body || '').trim().length < 10) return show('body_too_short');
    const button = form.querySelector('button[type="submit"]');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    try {
      const result = await this.request(this.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: this.dataset.productId, rating: Number(values.rating), title: values.title, body: values.body, author: values.author }),
      });
      if (!result?.ok) return show(result?.error);
      await this.load(1);
      this.write.innerHTML = `<p class="kunnie-reviews__note" role="status">${escapeHtml(this.texts.thanks)}</p>`;
    } catch {
      show('default');
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  productHtml(product) {
    if (!product?.title) return '';
    const image = product.image ? `<img src="${escapeHtml(product.image)}" alt="" width="48" height="48" loading="lazy">` : '';
    const inner = `${image}<span>${escapeHtml(product.title)}</span>`;
    const root = window.Shopify?.routes?.root || '/';
    const url = product.url?.startsWith('/products/') ? `${root}${product.url.slice(1)}` : product.url;
    return url
      ? `<a class="kunnie-reviews__product" href="${escapeHtml(url)}">${inner}</a>`
      : `<p class="kunnie-reviews__product">${inner}</p>`;
  }

  reviewHtml(review) {
    return `
      <li class="kunnie-reviews__item">
        ${this.shop ? this.productHtml(review.product) : ''}
        <span class="kunnie-rating kunnie-rating--item" aria-label="${escapeHtml(fill(this.texts.stars, { count: review.rating }))}">${stars(review.rating)}</span>
        ${review.title ? `<h3 class="kunnie-reviews__item-title">${escapeHtml(review.title)}</h3>` : ''}
        <p class="kunnie-reviews__item-body">${escapeHtml(review.body)}</p>
        <p class="kunnie-reviews__meta"><span>${escapeHtml(review.author)}</span><span>${dateText(review.date)}</span>${review.verified ? `<span class="kunnie-reviews__verified">${escapeHtml(this.texts.verified)}</span>` : ''}</p>
      </li>`;
  }
}

if (!customElements.get('kunnie-reviews')) customElements.define('kunnie-reviews', KunnieReviews);
